import { apiClient } from '../api-client'
import { Ticket, TicketOverview, CreateTicketRequest, PaginatedResponse } from '@/types'
import { fileStorageApi } from './file-storage-api'

function normalizeTicketResponse(ticket: any): Ticket {
  return {
    ...ticket,
    id: String(ticket?.id || ''),
    ticket_number: ticket?.ticket_number || ticket?.ticketNumber,
    project_id: ticket?.project_id || ticket?.projectId,
    amc_contract_id: ticket?.amc_contract_id || ticket?.amcContractId,
    client_id: ticket?.client_id || ticket?.clientId,
    createdAt: ticket?.createdAt || ticket?.created_at,
    updatedAt: ticket?.updatedAt || ticket?.updated_at,
  } as Ticket
}

function normalizeTicketListResponse(response: any, page: number, limit: number): PaginatedResponse<Ticket> {
  if (Array.isArray(response)) {
    return {
      data: response.map(normalizeTicketResponse),
      total: response.length,
      page,
      limit,
      totalPages: 1,
    }
  }

  const rows = Array.isArray(response?.data) ? response.data : []
  return {
    data: rows.map(normalizeTicketResponse),
    total: Number(response?.total || rows.length),
    page: Number(response?.page || page),
    limit: Number(response?.limit || limit),
    totalPages: Number(response?.totalPages || response?.total_pages || 1),
  }
}

// Tickets API
export const ticketsApi = {
  getTickets: async (filters?: any, page = 1, limit = 20): Promise<PaginatedResponse<Ticket>> => {
    const response = await apiClient.get('/tickets', { params: { ...filters, page, limit } })
    return normalizeTicketListResponse(response, page, limit)
  },

  getOverview: (): Promise<TicketOverview> =>
    apiClient.get('/tickets/overview'),

  getTicket: (id: string): Promise<Ticket> =>
    apiClient.get(`/tickets/${id}`).then(normalizeTicketResponse),

  createTicket: (data: CreateTicketRequest): Promise<Ticket> =>
    apiClient.post('/tickets', data).then(normalizeTicketResponse),

  updateTicket: (id: string, data: Partial<Ticket>): Promise<Ticket> =>
    apiClient.put(`/tickets/${id}`, data).then(normalizeTicketResponse),

  deleteTicket: (id: string): Promise<void> =>
    apiClient.delete(`/tickets/${id}`),

  updateStatus: (id: string, status: Ticket['status']): Promise<Ticket> =>
    apiClient.patch(`/tickets/${id}/status`, { status }),

  assignTicket: (id: string, userId: string): Promise<Ticket> =>
    apiClient.patch(`/tickets/${id}/assign`, { userId }),

  addComment: (id: string, content: string, attachments?: File[]): Promise<any> =>
    apiClient.post(`/tickets/${id}/comments`, { content, attachments }),

  escalateTicket: (id: string, reason: string): Promise<Ticket> =>
    apiClient.post(`/tickets/${id}/escalate`, { reason }),

  uploadAttachment: async (ticketId: string, file: File, projectId?: string): Promise<any> => {
    let resolvedProjectId = String(projectId || '').trim()
    if (!resolvedProjectId) {
      const ticket = await ticketsApi.getTicket(ticketId)
      resolvedProjectId = String((ticket as any)?.project_id || (ticket as any)?.projectId || '').trim()
    }

    if (!resolvedProjectId) {
      throw new Error('Project ID is required to upload a ticket attachment')
    }

    return fileStorageApi.uploadEntityAttachment({
      project_id: resolvedProjectId,
      category: 'tickets',
      entity_type: 'ticket',
      entity_id: ticketId,
    }, file)
  },
}
